import React, { useRef, useEffect, MutableRefObject } from "react";

const DEFAULT_EVENTS = ["click"];
//eslint-disable-next-line @typescript-eslint/no-explicit-any
const DEFAULT_IGNORED_ELEMENTS: MutableRefObject<any>[] = [];

type useClickOutsideOptions = {
  callback: () => void;
  activate: boolean;
  events?: string[];
  eventListenerOptions?: Parameters<Document["addEventListener"]>[2];
  onlyOnce?: boolean;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  ignoredElements?: MutableRefObject<any>[];
  ignoreTargetSelector?: string;
};

export function useClickOutside<T extends HTMLElement>({
  callback,
  activate,
  events = DEFAULT_EVENTS,
  eventListenerOptions = true,
  onlyOnce = true,
  ignoredElements = DEFAULT_IGNORED_ELEMENTS,
  ignoreTargetSelector,
}: useClickOutsideOptions): MutableRefObject<T | null> {
  const elementRef = useRef<T | null>(null);

  useEffect(() => {
    const onClickOutside = (event: Event) => {
      const eventInIgnoredElement = [...ignoredElements, elementRef].some((element) => element.current?.contains(event.target as Element));
      if (eventInIgnoredElement) {
        return;
      }
      if (ignoreTargetSelector && (event.target as Element).closest?.(ignoreTargetSelector)) {
        return;
      }
      if (onlyOnce) {
        removeBodyEventListener();
      }
      callback();
    };

    function removeBodyEventListener() {
      events.forEach((e) => document.removeEventListener(e, onClickOutside, eventListenerOptions));
    }

    if (activate) {
      events.forEach((e) => document.addEventListener(e, onClickOutside, eventListenerOptions));
    } else {
      removeBodyEventListener();
    }
    return () => removeBodyEventListener();
  }, [activate, events, callback, eventListenerOptions, onlyOnce, ignoredElements, ignoreTargetSelector]);

  return elementRef;
}

export const getHighlightedText = (text: string, term?: string) => {
  const parts = text.split(new RegExp(`(${term})`, "gi"));
  if (!term) {
    return <span>{text}</span>;
  }
  return (
    <span>
      {" "}
      {parts.map((part: string, i: number) => (
        <pre key={i} style={part.toLowerCase() === term.toLowerCase() ? { background: "yellow" } : {}}>
          {part}
        </pre>
      ))}{" "}
    </span>
  );
};

export const isDateToday = (date: Date | undefined) => {
  const otherDate = new Date(date || "");
  const todayDate = new Date();
  return (
    otherDate.getDate() === todayDate.getDate() &&
    otherDate.getMonth() === todayDate.getMonth() &&
    otherDate.getFullYear() === todayDate.getFullYear()
  );
};

export const downloadBlob = (content: string, filename: string, contentType: string) => {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);

  downloadFileByUrl(filename, url);
};

export const downloadFileByUrl = (filename: string, url: string) => {
  // Create a link to download it
  const pom = document.createElement("a");
  pom.href = url;
  pom.setAttribute("download", filename);
  pom.click();
};
